/**
 * Domain status / type code catalogs — mirror BE `TmFood.Domain` string consts.
 * SoT remains C# Domain; keep wire values identical (PascalCase / existing codes).
 * Presentation (VI labels / tone) stays in `statusLabels.ts` — do not put VI here.
 */

/** MDM / SYS / ENG enablement (not version lifecycle). BE `EnablementStatuses`. */
export const EnablementStatuses = {
  Active: "Active",
  Inactive: "Inactive",
} as const;
export type EnablementStatus =
  (typeof EnablementStatuses)[keyof typeof EnablementStatuses];

/** Batch EBR close state. BE `EbrStatuses`. */
export const EbrStatuses = {
  Open: "Open",
  Closed: "Closed",
} as const;
export type EbrStatus = (typeof EbrStatuses)[keyof typeof EbrStatuses];

/** Stock / material reservation. BE `ReservationStatuses`. */
export const ReservationStatuses = {
  Open: "Open",
  Closed: "Closed",
} as const;
export type ReservationStatus =
  (typeof ReservationStatuses)[keyof typeof ReservationStatuses];

/** BE `SalesOrderStatuses`. */
export const SalesOrderStatuses = {
  Draft: "Draft",
  Confirmed: "Confirmed",
  Planned: "Planned",
  InProduction: "InProduction",
  Ready: "Ready",
  Completed: "Completed",
  OnHold: "OnHold",
  Cancelled: "Cancelled",
} as const;
export type SalesOrderStatus =
  (typeof SalesOrderStatuses)[keyof typeof SalesOrderStatuses];
export const SalesOrderStatusValues = Object.values(SalesOrderStatuses);

/** BE `ShipmentStatuses`. */
export const ShipmentStatuses = {
  Draft: "Draft",
  Confirmed: "Confirmed",
  Shipped: "Shipped",
  Cancelled: "Cancelled",
} as const;
export type ShipmentStatus =
  (typeof ShipmentStatuses)[keyof typeof ShipmentStatuses];
export const ShipmentStatusValues = Object.values(ShipmentStatuses);

/** BE `SalesFulfillmentTypes`. */
export const SalesFulfillmentTypes = {
  Mto: "MTO",
  Mts: "MTS",
} as const;
export type SalesFulfillmentType =
  (typeof SalesFulfillmentTypes)[keyof typeof SalesFulfillmentTypes];

/** BE `SalesOrderPriorities`. */
export const SalesOrderPriorities = {
  Normal: "Normal",
  Urgent: "Urgent",
  Critical: "Critical",
} as const;
export type SalesOrderPriority =
  (typeof SalesOrderPriorities)[keyof typeof SalesOrderPriorities];

/** BE `PurchaseRequisitionStatuses`. */
export const PurchaseRequisitionStatuses = {
  Draft: "Draft",
  Submitted: "Submitted",
  Approved: "Approved",
  Rejected: "Rejected",
  Converted: "Converted",
} as const;
export type PurchaseRequisitionStatus =
  (typeof PurchaseRequisitionStatuses)[keyof typeof PurchaseRequisitionStatuses];
export const PurchaseRequisitionStatusValues = Object.values(
  PurchaseRequisitionStatuses,
);

/** BE `PurchaseOrderStatuses`. */
export const PurchaseOrderStatuses = {
  Draft: "Draft",
  Confirmed: "Confirmed",
  PartiallyReceived: "PartiallyReceived",
  Received: "Received",
  Closed: "Closed",
  OnHold: "OnHold",
  Cancelled: "Cancelled",
} as const;
export type PurchaseOrderStatus =
  (typeof PurchaseOrderStatuses)[keyof typeof PurchaseOrderStatuses];
export const PurchaseOrderStatusValues = Object.values(PurchaseOrderStatuses);

/** BE `DemandStatuses`. */
export const DemandStatuses = {
  Open: "Open",
  Planned: "Planned",
  Closed: "Closed",
  Cancelled: "Cancelled",
} as const;
export type DemandStatus = (typeof DemandStatuses)[keyof typeof DemandStatuses];
export const DemandStatusValues = Object.values(DemandStatuses);

/** BE `DemandSourceTypes`. */
export const DemandSourceTypes = {
  SalesOrder: "SalesOrder",
  Manual: "Manual",
} as const;
export type DemandSourceType =
  (typeof DemandSourceTypes)[keyof typeof DemandSourceTypes];

/** BE `ProductionOrderStatuses`. */
export const ProductionOrderStatuses = {
  Draft: "Draft",
  Released: "Released",
  InProgress: "InProgress",
  Completed: "Completed",
  Cancelled: "Cancelled",
} as const;
export type ProductionOrderStatus =
  (typeof ProductionOrderStatuses)[keyof typeof ProductionOrderStatuses];
export const ProductionOrderStatusValues = Object.values(
  ProductionOrderStatuses,
);

/** BE `BatchSizingModes`. */
export const BatchSizingModes = {
  Exact: "EXACT",
  FullBatches: "FULL_BATCHES",
  Manual: "MANUAL",
} as const;
export type BatchSizingMode =
  (typeof BatchSizingModes)[keyof typeof BatchSizingModes];

/** BE `ReadinessStatuses`. */
export const ReadinessStatuses = {
  Pending: "Pending",
  Ready: "Ready",
  Blocked: "Blocked",
} as const;
export type ReadinessStatus =
  (typeof ReadinessStatuses)[keyof typeof ReadinessStatuses];

/** BE `BatchStatuses`. */
export const BatchStatuses = {
  Planned: "Planned",
  Released: "Released",
  Issued: "Issued",
  InProgress: "InProgress",
  Hold: "Hold",
  Completed: "Completed",
  Cancelled: "Cancelled",
} as const;
export type BatchStatus = (typeof BatchStatuses)[keyof typeof BatchStatuses];
export const BatchStatusValues = Object.values(BatchStatuses);

/** BE `OperationExecutionStatuses`. */
export const OperationExecutionStatuses = {
  Pending: "Pending",
  InProgress: "InProgress",
  Paused: "Paused",
  Completed: "Completed",
  Skipped: "Skipped",
  Blocked: "Blocked",
  Cancelled: "Cancelled",
} as const;
export type OperationExecutionStatus =
  (typeof OperationExecutionStatuses)[keyof typeof OperationExecutionStatuses];

/** BE `HoldStatuses`. */
export const HoldStatuses = {
  Open: "Open",
  Released: "Released",
} as const;
export type HoldStatus = (typeof HoldStatuses)[keyof typeof HoldStatuses];
export const HoldStatusValues = Object.values(HoldStatuses);

/** BE `HandoffStatuses`. */
export const HandoffStatuses = {
  Completed: "Completed",
  Blocked: "Blocked",
} as const;
export type HandoffStatus =
  (typeof HandoffStatuses)[keyof typeof HandoffStatuses];

/** BE `CipStatuses`. */
export const CipStatuses = {
  Planned: "Planned",
  InProgress: "InProgress",
  Completed: "Completed",
  Failed: "Failed",
} as const;
export type CipStatus = (typeof CipStatuses)[keyof typeof CipStatuses];

/** BE `ReworkOrderStatuses`. */
export const ReworkOrderStatuses = {
  Open: "Open",
  Done: "Done",
  Cancelled: "Cancelled",
} as const;
export type ReworkOrderStatus =
  (typeof ReworkOrderStatuses)[keyof typeof ReworkOrderStatuses];

/** BE `ParamSpecStates`. */
export const ParamSpecStates = {
  InSpec: "InSpec",
  Warning: "Warning",
  Breach: "Breach",
} as const;
export type ParamSpecState =
  (typeof ParamSpecStates)[keyof typeof ParamSpecStates];

/** BE `QcStatuses` (lot QC). */
export const QcStatuses = {
  Pending: "Pending",
  Passed: "Passed",
  Failed: "Failed",
  Conditional: "Conditional",
} as const;
export type QcStatus = (typeof QcStatuses)[keyof typeof QcStatuses];
export const QcStatusValues = Object.values(QcStatuses);

/** BE `InventoryStatuses`. */
export const InventoryStatuses = {
  Available: "Available",
  Reserved: "Reserved",
  Hold: "Hold",
  Quarantine: "Quarantine",
  Blocked: "Blocked",
  Expired: "Expired",
  Consumed: "Consumed",
  Disposed: "Disposed",
} as const;
export type InventoryStatus =
  (typeof InventoryStatuses)[keyof typeof InventoryStatuses];
export const InventoryStatusValues = Object.values(InventoryStatuses);

/** BE `TxnStatuses`. */
export const TxnStatuses = {
  Draft: "Draft",
  PendingApproval: "PendingApproval",
  Posted: "Posted",
  Void: "Void",
} as const;
export type TxnStatus = (typeof TxnStatuses)[keyof typeof TxnStatuses];
export const TxnStatusValues = Object.values(TxnStatuses);

/** BE `TxnTypes`. */
export const TxnTypes = {
  Gr: "GR",
  Gi: "GI",
  Transfer: "TRANSFER",
  Adjust: "ADJUST",
  Disposal: "DISPOSAL",
  Reversal: "REVERSAL",
} as const;
export type TxnType = (typeof TxnTypes)[keyof typeof TxnTypes];

/** BE `LotLinkTypes`. */
export const LotLinkTypes = {
  Consume: "consume",
  Produce: "produce",
  Split: "split",
  Merge: "merge",
  Rework: "rework",
  Repack: "repack",
  Scrap: "scrap",
} as const;
export type LotLinkType = (typeof LotLinkTypes)[keyof typeof LotLinkTypes];

/** BE `CategoryCodes`. */
export const CategoryCodes = {
  PlanType: "PLAN_TYPE",
} as const;
export type CategoryCode = (typeof CategoryCodes)[keyof typeof CategoryCodes];

/** BE `InspectionTypes`. */
export const InspectionTypes = {
  Iqc: "IQC",
  Ipqc: "IPQC",
  Fqc: "FQC",
  Sort: "SORT",
  Hygiene: "HYGIENE",
} as const;
export type InspectionType =
  (typeof InspectionTypes)[keyof typeof InspectionTypes];

/** BE `InspectionResults`. */
export const InspectionResults = {
  Pass: "Pass",
  Fail: "Fail",
  Conditional: "Conditional",
  Pending: "Pending",
} as const;
export type InspectionResult =
  (typeof InspectionResults)[keyof typeof InspectionResults];

/** BE `QaReleaseDecisions`. */
export const QaReleaseDecisions = {
  Release: "Release",
  Reject: "Reject",
  Conditional: "Conditional",
  Hold: "Hold",
} as const;
export type QaReleaseDecision =
  (typeof QaReleaseDecisions)[keyof typeof QaReleaseDecisions];

/** BE `NcrStatuses`. */
export const NcrStatuses = {
  Open: "Open",
  DispositionPending: "DispositionPending",
  Closed: "Closed",
  Cancelled: "Cancelled",
} as const;
export type NcrStatus = (typeof NcrStatuses)[keyof typeof NcrStatuses];
export const NcrStatusValues = Object.values(NcrStatuses);

/** BE `DeviationStatuses`. */
export const DeviationStatuses = {
  Open: "Open",
  Closed: "Closed",
} as const;
export type DeviationStatus =
  (typeof DeviationStatuses)[keyof typeof DeviationStatuses];
export const DeviationStatusValues = Object.values(DeviationStatuses);

/** BE `VersionStatuses` (ENG versioned masters). */
export const VersionStatuses = {
  Draft: "Draft",
  PendingApproval: "PendingApproval",
  Approved: "Approved",
  Effective: "Effective",
  Obsolete: "Obsolete",
} as const;
export type VersionStatus =
  (typeof VersionStatuses)[keyof typeof VersionStatuses];
export const VersionStatusValues = Object.values(VersionStatuses);

/** BE `FormInstanceStatuses`. */
export const FormInstanceStatuses = {
  Draft: "Draft",
  InProgress: "InProgress",
  Completed: "Completed",
  Cancelled: "Cancelled",
} as const;
export type FormInstanceStatus =
  (typeof FormInstanceStatuses)[keyof typeof FormInstanceStatuses];

/** BE `ApprovalInstanceStatuses` / step statuses (same tokens). */
export const ApprovalInstanceStatuses = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
} as const;
export type ApprovalInstanceStatus =
  (typeof ApprovalInstanceStatuses)[keyof typeof ApprovalInstanceStatuses];

/** BE `ItemTypes`. */
export const ItemTypes = {
  Rm: "RM",
  Sfg: "SFG",
  Fg: "FG",
  Pkg: "PKG",
  Rework: "REWORK",
  Consumable: "CONSUMABLE",
  Other: "OTHER",
} as const;
export type ItemType = (typeof ItemTypes)[keyof typeof ItemTypes];

/** BE `PartnerTypes`. */
export const PartnerTypes = {
  Customer: "customer",
  Supplier: "supplier",
  Both: "both",
} as const;
export type PartnerType = (typeof PartnerTypes)[keyof typeof PartnerTypes];

/** BE `SeverityLevels` (NCR / EXE approval). */
export const SeverityLevels = {
  Low: "Low",
  Medium: "Medium",
  High: "High",
  Critical: "Critical",
} as const;
export type SeverityLevel =
  (typeof SeverityLevels)[keyof typeof SeverityLevels];
export const SeverityLevelValues = Object.values(SeverityLevels);

/** BE `NotificationSeverities` (in-app / push). */
export const NotificationSeverities = {
  Info: "Info",
  Warning: "Warning",
  Error: "Error",
} as const;
export type NotificationSeverity =
  (typeof NotificationSeverities)[keyof typeof NotificationSeverities];

export const EnablementStatusValues = Object.values(EnablementStatuses);
export const ReservationStatusValues = Object.values(ReservationStatuses);
